import styled from 'styled-components';
import BackButton from '../utils/back_button';
import { new_educator_blog_2, ai_study_assistants, automated_feedback, analytics_dashboard, personalized_learning_path, smart_scheduling_tools } from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower";

export default function Blog24() {

  return (
    <>
     <Fullpage>
      <PageBackground>
        <BlogContainer>
          <BlogWrapper>
            <BlogHeader
              title="How AI‑Powered LMS Tools Empower Educators to Improve Student Performance and Support On‑Time Graduation"
              date="19 May 2026"
              author="posted by campus insights"
            />
            <BlogContent />
          </BlogWrapper>
        </BlogContainer>
        <OtherBlogs currentBlogId={24} type="ai-lms" />
     </PageBackground>
     </Fullpage>
     <CTASection />
    </>
  );
};

const BlogHeader = ({ title, date, author }) => {
  const navigate = useNavigate();
  const handleBackClick = () => {
    window.scrollTo(0, 0);
    navigate("/blog");
  };
  return (
    <HeaderContainer>
      <HeaderContent>
        <BackButtonWrapper>
          <BackButton onClick={handleBackClick} />
        </BackButtonWrapper>
        <MainTitle>{title}</MainTitle>
        <MetadataContainer>
          <MetadataWrapper>
            <MetadataContent>
              <DateText>{date}</DateText>
              <AuthorText>{author}</AuthorText>
            </MetadataContent>
          </MetadataWrapper>
        </MetadataContainer>
      </HeaderContent>
      <HeroImage src={new_educator_blog_2} />
      <IntroText>
        Educators today face a growing challenge: helping diverse students succeed on time while managing increasing class sizes, administrative demands, and limited support resources. AI-powered LMS tools are changing that equation—giving instructors the data, automation, and student engagement features they need to make a measurable impact on every learner's journey.
      </IntroText>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <BlogSection title="Personalized Learning Paths: Scaling Differentiated Instruction" ismainsection="true">
        <SectionImage src={personalized_learning_path} alt="Personalized Learning Paths" />
        <ContentText>
          Traditional instruction often means teaching to the middle—leaving advanced students unchallenged and struggling students behind. AI-powered LMS platforms change this by automatically generating personalized learning paths based on each student's performance, pace, and learning style. Educators can set course outcomes and let the system adapt content delivery accordingly.
        </ContentText>
        <BulletList>
          <BulletItem>Adaptive modules that adjust difficulty based on quiz performance and engagement data</BulletItem>
          <BulletItem>Recommended resources surfaced automatically for students who need additional support</BulletItem>
          <BulletItem>Accelerated pathways for high-performing students to stay engaged and challenged</BulletItem>
        </BulletList>
      </BlogSection>

      <BlogSection title="Smart Scheduling Tools: Reducing Student Overwhelm and Instructor Follow-Up" ismainsection="true">
        <SectionImage src={smart_scheduling_tools} alt="Smart Scheduling Tools" />
        <ContentText>
          One of the leading causes of academic underperformance is poor time management. When students miss deadlines or lose track of upcoming assignments, instructors spend hours chasing follow-ups. AI scheduling tools within the LMS solve this at the source—automatically organizing student workloads and sending proactive reminders before issues escalate.
        </ContentText>
        <BulletList>
          <BulletItem>AI-generated study schedules based on assignment due dates and estimated completion time</BulletItem>
          <BulletItem>Automated nudges sent to students before key deadlines to reduce late submissions</BulletItem>
          <BulletItem>Instructor dashboards showing which students are falling behind on time-sensitive tasks</BulletItem>
        </BulletList>
      </BlogSection>

      <BlogSection title="AI Study Assistants: Enhancing Student Preparedness Before Class" ismainsection="true">
        <SectionImage src={ai_study_assistants} alt="AI Study Assistants" />
        <ContentText>
          When students arrive to class underprepared, instructors lose valuable instructional time reviewing material that should already be understood. AI study assistants embedded in the LMS help students review concepts, test their knowledge, and ask questions on their own schedule—so class time can be used for deeper discussion and application.
        </ContentText>
        <BulletList>
          <BulletItem>24/7 conversational AI tutors that answer content-specific questions between sessions</BulletItem>
          <BulletItem>Practice quizzes and concept reviews personalized to each student's weak areas</BulletItem>
          <BulletItem>Pre-class preparation prompts that prime students for upcoming lecture topics</BulletItem>
        </BulletList>
      </BlogSection>

      <BlogSection title="Automated Feedback: Supporting Faster Skill Development" ismainsection="true">
        <SectionImage src={automated_feedback} alt="Automated Feedback" />
        <ContentText>
          Timely feedback is one of the most powerful drivers of student growth—yet manual grading delays often mean students don't receive feedback until long after the learning moment has passed. AI-powered automated feedback closes this gap by providing immediate, specific guidance on assignments, quizzes, and open-ended responses.
        </ContentText>
        <BulletList>
          <BulletItem>Instant feedback on objective assessments so students can correct misunderstandings immediately</BulletItem>
          <BulletItem>AI-assisted grading for written work that highlights areas for improvement with suggested revisions</BulletItem>
          <BulletItem>Performance trend reports shared with students to reinforce self-awareness and accountability</BulletItem>
        </BulletList>
      </BlogSection>

      <BlogSection title="Progress Dashboards & Predictive Analytics: Enabling Proactive Intervention" ismainsection="true">
        <SectionImage src={analytics_dashboard} alt="Progress Dashboards and Predictive Analytics" />
        <ContentText>
          The biggest risk to on-time graduation is intervention that comes too late. By the time a student is visibly struggling, the damage to their academic trajectory may already be significant. AI-powered progress dashboards give educators real-time visibility into student performance trends—and predictive analytics flag at-risk students before they fall off track.
        </ContentText>
        <BulletList>
          <BulletItem>Visual dashboards showing individual and cohort-level performance across assessments and participation</BulletItem>
          <BulletItem>Predictive risk scores that identify students likely to disengage or miss graduation milestones</BulletItem>
          <BulletItem>Automated alerts that prompt instructors to reach out to flagged students with targeted support</BulletItem>
        </BulletList>
      </BlogSection>

      <BlogSection title="The Educator Advantage: More Insight, Less Guesswork" ismainsection="true">
        <ContentText>
          AI-powered LMS tools don't replace the educator's role—they elevate it. By automating routine tasks and surfacing actionable insights, these platforms give instructors the clarity and capacity to do what matters most: building relationships, fostering critical thinking, and guiding students toward successful outcomes. The result is a more empowered educator and a better-supported student body.
        </ContentText>
        <BulletList>
          <BulletItem>Reduced time spent on grading, scheduling coordination, and manual follow-up communications</BulletItem>
          <BulletItem>Data-backed confidence when making instructional decisions or identifying students who need support</BulletItem>
          <BulletItem>Greater ability to personalize instruction without sacrificing course consistency or rigor</BulletItem>
          <BulletItem>Clear documentation of student progress to support academic advising and retention conversations</BulletItem>
          <BulletItem>A teaching environment where no student falls through the cracks undetected</BulletItem>
        </BulletList>
      </BlogSection>
    </ContentContainer>
  );
};

const BlogSection = ({ title, children, ismainsection = "false" }) => {
  return (
    <SectionContainer>
      {title && (
        <SectionTitle ismainsection={ismainsection}>
          {title}
        </SectionTitle>
      )}
      <SectionContent>{children}</SectionContent>
    </SectionContainer>
  );
};

const SectionImage = styled.img`
  width: 100%;
  border-radius: 12px;
  object-fit: cover;
`;

const BulletList = styled.ul`
  margin: 0;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BulletItem = styled.li`
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
`;

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const SectionTitle = styled.h2`
  align-self: stretch;
  color: ${props => props.ismainsection === "true" ? '#1c1b1f' : '#64636a'};
  letter-spacing: ${props => props.ismainsection === "true" ? '-0.32px' : '-0.2px'};
  font: ${props => props.ismainsection === "true" ? '700 32px/120% Inter, sans-serif' : '500 20px/140% Inter, sans-serif'};
  margin: 0;
  @media (max-width: 640px) {
    font-size: ${props => props.ismainsection === "true" ? '24px' : '18px'};
  }
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const ContentText = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MainTitle = styled.h1`
  width: 100%;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 36px;
    line-height: 120%;
  }
  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateText = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const AuthorText = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 500 16px/120% Inter, sans-serif;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const HeroImage = styled.img`
  display: flex;
  width: 100%;
  height: 346px;
  justify-content: flex-end;
  align-items: center;
  border-radius: 16px;
  object-fit: cover;
  object-position: top;
  @media (max-width: 991px) {
    height: 280px;
  }
  @media (max-width: 640px) {
    height: 200px;
  }
`;

const IntroText = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
`;

const BlogContainer = styled.article`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  max-width: 100%;
  box-sizing: border-box;
  background-color: #fff;
  @media (max-width: 991px) {
    width: 100%;
    padding: 40px 32px;
    gap: 48px;
  }
  @media (max-width: 640px) {
    padding: 24px 16px;
    gap: 32px;
  }
`;

const BlogWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
  flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 640px) {
    padding: 40px 0;
  }
`;
